import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const galleryImages = [
  {
    url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/dx0a3wdq_IMG_4852.jpeg",
    alt: "Paver patio with stone pillars and lighting",
    category: "Paver Patios"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/btl73w4y_IMG_0436.jpeg",
    alt: "Circular fire pit paver patio by the lake",
    category: "Paver Patios"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/bp8uwxqn_IMG_2279.jpeg",
    alt: "Backyard paver patio with decorative border",
    category: "Paver Patios"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/4u9w80e9_IMG_0718.jpeg",
    alt: "Commercial paver walkway",
    category: "Paver Patios"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/6ukm95zo_IMG_1454.jpeg",
    alt: "Lakefront paver patio with retaining wall",
    category: "Paver Patios"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/zydjz8ob_IMG_1430.jpeg",
    alt: "Paver patio installation",
    category: "Paver Patios"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/byhpznjq_IMG_1517.png",
    alt: "Front yard landscaping with retaining wall and mulch beds",
    category: "Retaining Walls"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/4kmk778h_Screenshot%202026-02-25%20at%203.47.03%E2%80%AFPM.png",
    alt: "Concrete patio installation",
    category: "Concrete"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/cz3w10y4_Screenshot%202026-02-25%20at%203.46.31%E2%80%AFPM.png",
    alt: "Arborvitae tree row installation",
    category: "Landscaping"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/tpx083x2_Screenshot%202026-02-25%20at%203.46.39%E2%80%AFPM.png",
    alt: "Professional lawn striping",
    category: "Landscaping"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/ja35jvxr_Screenshot%202026-02-25%20at%203.46.46%E2%80%AFPM.png",
    alt: "Retaining wall with landscaping",
    category: "Retaining Walls"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/51zgzflw_Screenshot%202026-02-25%20at%203.46.56%E2%80%AFPM.png",
    alt: "Front yard landscaping with mulch beds",
    category: "Landscaping"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/1l72nh65_IMG_0205.jpeg",
    alt: "Snow plow truck in action",
    category: "Snow Removal"
  }
];

export const ProjectGallery = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  return (
    <section id="project-gallery" className="py-20 md:py-28 bg-[#F9FAFB]" data-testid="project-gallery">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-[#2F5930] font-semibold mb-3 tracking-wide uppercase text-sm">
            Our Work
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1F2937] mb-4 font-['Manrope']">
            See Our Projects
          </h2>
          <p className="text-lg text-[#4B5563] max-w-2xl mx-auto">
            Browse through our completed landscaping, paver, and hardscape projects 
            throughout Joliet and surrounding areas.
          </p>
        </div>

        {/* Main Slideshow */}
        <div className="relative max-w-4xl mx-auto">
          <div className="relative aspect-[16/10] rounded-2xl overflow-hidden shadow-xl">
            <img
              src={galleryImages[currentIndex].url}
              alt={galleryImages[currentIndex].alt}
              className="w-full h-full object-cover transition-opacity duration-500"
            />
            {/* Category Badge */}
            <div className="absolute top-4 left-4 bg-[#2F5930] text-white px-4 py-1.5 rounded-full text-sm font-medium">
              {galleryImages[currentIndex].category}
            </div>
          </div>

          {/* Navigation Arrows */}
          <Button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-[#1F2937] rounded-full w-12 h-12 p-0 shadow-lg"
            data-testid="gallery-prev"
          >
            <ChevronLeft className="w-6 h-6" />
          </Button>
          <Button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-[#1F2937] rounded-full w-12 h-12 p-0 shadow-lg"
            data-testid="gallery-next"
          >
            <ChevronRight className="w-6 h-6" />
          </Button>

          {/* Image Counter */}
          <div className="absolute bottom-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
            {currentIndex + 1} / {galleryImages.length}
          </div>
        </div>

        {/* Thumbnail Navigation */}
        <div className="flex justify-center gap-2 mt-6 flex-wrap">
          {galleryImages.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? "bg-[#2F5930] w-8" 
                  : "bg-gray-300 hover:bg-gray-400"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Thumbnail Grid Preview */}
        <div className="grid grid-cols-5 md:grid-cols-10 gap-2 mt-8">
          {galleryImages.map((image, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`aspect-square rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                index === currentIndex 
                  ? "border-[#2F5930] ring-2 ring-[#2F5930]/30" 
                  : "border-transparent hover:border-gray-300"
              }`}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};
